import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {DialogSubmitFooter} from '@/components/dialog-submit-footer'
import {useAnularFactura, useRegistrarPagoManual, type AdminInvoice} from '@/hooks/inci/cartera/use-cartera'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

/** `testPrefix` keeps each call site's `data-testid`s distinct for e2e. */
export function AnularFacturaDialog({
  target,
  onClose,
  testPrefix,
}: {
  target: AdminInvoice | null
  onClose: () => void
  testPrefix: string
}) {
  const {t} = useTranslation()
  const anular = useAnularFactura()
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(open: boolean) {
    if (!open) {
      setMotivo('')
      setError(null)
      anular.reset()
      onClose()
    }
  }

  function submit() {
    if (!target) return
    const trimmed = motivo.trim()
    if (!trimmed) {
      setError(t('cartera.facturas.motivoRequired'))
      return
    }
    anular.mutate(
      {invoiceId: target.id, motivo: trimmed},
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={handleOpenChange}>
      <DialogContent data-testid={`${testPrefix}-anular-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {target && t('cartera.facturas.anularDialogTitle', {numero: target.numero_factura})}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('cartera.facturas.anularDialogDescription')}</p>
        <div className="space-y-2">
          <Label htmlFor={`${testPrefix}-anular-motivo`}>{t('cartera.facturas.motivoLabel')}</Label>
          <Textarea
            id={`${testPrefix}-anular-motivo`}
            data-testid={`${testPrefix}-anular-motivo`}
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value)
              setError(null)
            }}
            maxLength={tope('NOTA')}
            minRows={3}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogSubmitFooter
          onCancel={() => handleOpenChange(false)}
          onSubmit={submit}
          submitLabel={t('cartera.facturas.anularSubmit')}
          pending={anular.isPending}
          destructive
          testId={`${testPrefix}-anular-submit`}
        />
      </DialogContent>
    </Dialog>
  )
}

export function PagoManualDialog({
  target,
  onClose,
  testPrefix,
}: {
  target: AdminInvoice | null
  onClose: () => void
  testPrefix: string
}) {
  const {t} = useTranslation()
  const registrar = useRegistrarPagoManual()
  const [referencia, setReferencia] = useState('')
  const [metodo, setMetodo] = useState('')
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(open: boolean) {
    if (!open) {
      setReferencia('')
      setMetodo('')
      setError(null)
      registrar.reset()
      onClose()
    }
  }

  function submit() {
    if (!target) return
    if (!referencia.trim()) {
      setError(t('cartera.facturas.referenciaRequired'))
      return
    }
    if (!metodo.trim()) {
      setError(t('cartera.facturas.metodoRequired'))
      return
    }
    registrar.mutate(
      {invoiceId: target.id, referenciaExterna: referencia.trim(), metodo: metodo.trim()},
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={handleOpenChange}>
      <DialogContent data-testid={`${testPrefix}-pago-manual-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {target && t('cartera.facturas.pagoManualDialogTitle', {numero: target.numero_factura})}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('cartera.facturas.pagoManualDialogDescription')}</p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${testPrefix}-pago-referencia`}>{t('cartera.facturas.referenciaLabel')}</Label>
            <Input
              maxLength={tope('NOTA')}
              id={`${testPrefix}-pago-referencia`}
              data-testid={`${testPrefix}-pago-referencia`}
              value={referencia}
              onChange={(e) => {
                setReferencia(e.target.value)
                setError(null)
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testPrefix}-pago-metodo`}>{t('cartera.facturas.metodoLabel')}</Label>
            <Input
              maxLength={tope('CODE')}
              id={`${testPrefix}-pago-metodo`}
              data-testid={`${testPrefix}-pago-metodo`}
              value={metodo}
              onChange={(e) => {
                setMetodo(e.target.value)
                setError(null)
              }}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogSubmitFooter
          onCancel={() => handleOpenChange(false)}
          onSubmit={submit}
          submitLabel={t('cartera.facturas.pagoManualSubmit')}
          pending={registrar.isPending}
          testId={`${testPrefix}-pago-manual-submit`}
        />
      </DialogContent>
    </Dialog>
  )
}

export function FacturaRowActions({
  invoice,
  onAnular,
  onPagoManual,
  testPrefix,
}: {
  invoice: AdminInvoice
  onAnular: (invoice: AdminInvoice) => void
  onPagoManual: (invoice: AdminInvoice) => void
  testPrefix: string
}) {
  const {t} = useTranslation()
  const anulable = invoice.estado === 'emitida' || invoice.estado === 'vencida'
  if (!anulable) {
    return invoice.estado === 'pagada' ? (
      <span className="text-xs text-muted-foreground" data-testid={`${testPrefix}-anular-bloqueada-${invoice.id}`}>
        {t('cartera.facturas.anularBlockedPagada')}
      </span>
    ) : null
  }
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      <Button
        size="sm"
        variant="outline"
        data-testid={`${testPrefix}-pago-manual-${invoice.id}`}
        onClick={() => onPagoManual(invoice)}>
        {t('cartera.facturas.pagoManual')}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="text-destructive hover:text-destructive"
        data-testid={`${testPrefix}-anular-${invoice.id}`}
        onClick={() => onAnular(invoice)}>
        {t('cartera.facturas.anular')}
      </Button>
    </div>
  )
}
